import { useContext, useState, useMemo, useEffect, useCallback } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { ActividadContext } from "../../../../context/ActividadContext";
import { updateContrato } from "../../../../application/contratos";
import { calcularIndiceAcumulado } from "../../../../application/indices";
import { fetchIPCHistorial, calcularAjusteICL } from "../../../../application/arglyApi";
import { formatMonto, diasHastaFecha, calcularComision, calcularTotalPropietario, calcularProximoAumento, formatFechaCorta } from "../../../../utils/rentalCalculations";
import { parseLocalDate, calcularFactorIPC, fechaPublicacionEstimada, nombreMes } from "../../../../utils/ipcCalculations";
import { FaTimes, FaCalculator, FaInfoCircle, FaGlobe, FaCheckCircle, FaTimesCircle, FaArrowUp, FaSearch, FaExclamationTriangle, FaFilePdf, FaReceipt } from "react-icons/fa";
import { Link } from "react-router-dom";
import AjusteShareModal from "../../../../components/AjusteCard/AjusteShareModal";
import { saveAumentoAplicado } from "../../../../application/aumentosAplicados";
import ModalPortal from "../../../../components/ModalPortal";
import { calcularHistorialAjustes } from "../../../../utils/historialAjustes";
import { agregarTramoMonto } from "../../../../utils/montoHistorico";
import { useSortableData } from "../../../../hooks/useSortableData";
import SortableHeader from "../../../../components/SortableHeader";
import MoneyInput from "../../../../components/MoneyInput";
import { agruparPorClave } from "../../../../utils/agrupar";
import { nombreCompleto } from "../../../../utils/nombre";

const FREQ_MESES = {
  TRIMESTRAL: 3,
  CUATRIMESTRAL: 4,
  SEMESTRAL: 6,
  ANUAL: 12,
};

const FREQ_LABELS = {
  TRIMESTRAL: "3 meses",
  CUATRIMESTRAL: "4 meses",
  SEMESTRAL: "6 meses",
  ANUAL: "12 meses",
};

// "hasta" = proximoAumento (next adjustment date). "desde" = proximoAumento -
// frecuencia (current cycle's start). The accumulated IPC covers the months
// [desde, hasta) -- i.e. desde month inclusive, hasta month exclusive (so a
// 4-month cycle Feb 1 -> Jun 1 consumes Feb, Mar, Apr, May).
const getPeriodoAumento = (contrato) => {
  const frecuencia = contrato.aumento?.frecuencia || "TRIMESTRAL";
  const mesesFrec = FREQ_MESES[frecuencia] || 4;
  const proximoAumento = contrato.aumento?.proximoAumento;
  if (!proximoAumento) return null;

  const fechaHasta = parseLocalDate(proximoAumento);
  if (!fechaHasta) return null;
  const fechaDesde = new Date(fechaHasta);
  fechaDesde.setMonth(fechaDesde.getMonth() - mesesFrec);

  return {
    desdeMes: fechaDesde.getMonth() + 1,
    desdeAnio: fechaDesde.getFullYear(),
    hastaMes: fechaHasta.getMonth() + 1,
    hastaAnio: fechaHasta.getFullYear(),
    fechaDesde,
    fechaHasta,
  };
};

function Aumentos() {
  const { contratos, locadores, locatarios, pagos, indices, aumentosAplicados, refreshContratos, refreshAumentosAplicados } = useContext(AlquileresContext);
  const { registrarActividad } = useContext(ActividadContext);
  const [filtro, setFiltro] = useState("TODOS");
  const [search, setSearch] = useState("");
  const [agrupar, setAgrupar] = useState(false);
  const [modalContrato, setModalContrato] = useState(null);
  const [nuevoMonto, setNuevoMonto] = useState("");
  const [montoCalculado, setMontoCalculado] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [saving, setSaving] = useState(false);
  // After applying, populate this with the snapshot to feed the share card
  const [ajusteAplicado, setAjusteAplicado] = useState(null);
  // Pre-calculated estimates for table display
  const [estimaciones, setEstimaciones] = useState({});
  const [cargandoEstimaciones, setCargandoEstimaciones] = useState(false);

  const now = new Date();
  const mesActual = now.getMonth() + 1;
  const anioActual = now.getFullYear();

  // Base list (no filter / no search) used both for the visible rows and for
  // the counts shown on each filter chip.
  const contratosBase = useMemo(() => {
    const nombrePorId = (lista) => {
      const m = {};
      lista.forEach((p) => { if (p.idFirebase) m[p.idFirebase] = nombreCompleto(p); });
      return m;
    };
    const locadorMap = nombrePorId(locadores);
    const locatarioMap = nombrePorId(locatarios);
    const activos = contratos.filter(
      (c) => c.estado === "ACTIVO" && c.aumento?.proximoAumento
    );
    return activos.map((c) => {
      const dias = diasHastaFecha(c.aumento.proximoAumento);
      const pagoMesActual = pagos.find(
        (p) => p.contratoId === c.idFirebase && p.mes === mesActual && p.anio === anioActual
      );
      let estadoPago = "PENDIENTE";
      if (pagoMesActual) {
        estadoPago = Number(pagoMesActual.montoPagado) >= Number(c.montoActual) ? "PAGADO" : "PARCIAL";
      }
      return {
        ...c,
        locadorNombre: c.locadorNombre || locadorMap[c.locadorId] || "",
        locatarioNombre: c.locatarioNombre || locatarioMap[c.locatarioId] || "",
        diasParaAumento: dias,
        estadoPago,
        pagoMesActual,
      };
    });
  }, [contratos, locadores, locatarios, pagos, mesActual, anioActual]);

  const conteos = useMemo(() => ({
    TODOS: contratosBase.length,
    VENCIDOS: contratosBase.filter((c) => c.diasParaAumento < 0).length,
    ESTE_MES: contratosBase.filter((c) => c.diasParaAumento >= 0 && c.diasParaAumento <= 30).length,
    PROXIMO_MES: contratosBase.filter((c) => c.diasParaAumento > 30 && c.diasParaAumento <= 60).length,
    PROXIMOS_3_MESES: contratosBase.filter((c) => c.diasParaAumento >= 0 && c.diasParaAumento <= 90).length,
  }), [contratosBase]);

  const contratosConAumento = useMemo(() => {
    return contratosBase
      .filter((c) => {
        if (filtro === "VENCIDOS") return c.diasParaAumento < 0;
        if (filtro === "ESTE_MES") return c.diasParaAumento >= 0 && c.diasParaAumento <= 30;
        if (filtro === "PROXIMO_MES") return c.diasParaAumento > 30 && c.diasParaAumento <= 60;
        if (filtro === "PROXIMOS_3_MESES") return c.diasParaAumento >= 0 && c.diasParaAumento <= 90;
        return true;
      })
      .filter((c) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
          c.locatarioNombre?.toLowerCase().includes(s) ||
          c.locadorNombre?.toLowerCase().includes(s) ||
          c.propiedad?.toLowerCase().includes(s)
        );
      });
  }, [contratosBase, filtro, search]);

  const getValue = useCallback((c, key) => {
    if (key === "estimado") return estimaciones[c.idFirebase]?.nuevoMonto ?? null;
    if (key === "porcentaje") return Number(estimaciones[c.idFirebase]?.porcentaje) || null;
    if (key === "diasParaAumento") {
      const d = c.diasParaAumento;
      return Number.isFinite(d) ? d : 999999;
    }
    return c?.[key];
  }, [estimaciones]);
  const { sortedData: sorted, sortConfig, requestSort } = useSortableData(contratosConAumento, {
    getValue,
    storageKey: "sort-aumentos",
    initial: { key: "diasParaAumento", direction: "asc" },
  });

  // Filas a renderizar. Con agrupado, intercala una fila de encabezado por
  // locador antes de sus contratos.
  const filasRender = useMemo(() => {
    if (!agrupar) return sorted.map((c) => ({ tipo: "fila", c }));
    const grupos = agruparPorClave(sorted, (c) => c.locadorNombre);
    const out = [];
    for (const [locador, items] of grupos) {
      out.push({ tipo: "grupo", locador, items });
      items.forEach((c) => out.push({ tipo: "fila", c }));
    }
    return out;
  }, [agrupar, sorted]);

  // Pre-calculate estimates for all visible contracts
  // Fetches IPC history ONCE, then calculates locally for each contract
  useEffect(() => {
    if (contratosConAumento.length === 0) return;
    const calcularTodos = async () => {
      setCargandoEstimaciones(true);
      const results = {};

      // Fetch IPC data once for all calculations
      let ipcData = [];
      try {
        ipcData = await fetchIPCHistorial();
      } catch (e) {
        console.warn("No se pudo obtener IPC:", e.message);
      }

      for (const c of contratosConAumento) {
        if (estimaciones[c.idFirebase]) {
          results[c.idFirebase] = estimaciones[c.idFirebase];
          continue;
        }
        try {
          const periodo = getPeriodoAumento(c);
          if (!periodo) { results[c.idFirebase] = null; continue; }
          const resultado = calcularFactorIPC(ipcData, periodo.fechaDesde, periodo.fechaHasta);

          if (resultado) {
            const nuevoMonto = Math.round(Number(c.montoActual) * resultado.factor);
            results[c.idFirebase] = {
              nuevoMonto,
              porcentaje: resultado.porcentaje,
              diferencia: nuevoMonto - Number(c.montoActual),
              parcial: resultado.aproximado,
              mesesUsados: resultado.publicados,
              totalMeses: resultado.totalMeses,
            };
          } else {
            results[c.idFirebase] = null;
          }
        } catch (e) {
          results[c.idFirebase] = null;
        }
      }
      setEstimaciones(results);
      setCargandoEstimaciones(false);
    };
    calcularTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratosConAumento.length]); // Only re-run when list changes

  const openAplicar = (contrato) => {
    setModalContrato(contrato);
    setNuevoMonto("");
    setMontoCalculado(null);
  };

  // Auto-calculate when modal opens
  useEffect(() => {
    if (!modalContrato) return;
    const calcular = async () => {
      setCalculando(true);
      try {
        const c = modalContrato;
        const tipoIndice = c.aumento?.tipo || "IPC";
        const periodo = getPeriodoAumento(c);

        if (!periodo) {
          setMontoCalculado({ tieneIndices: false, desde: "-", hasta: "-" });
          setCalculando(false);
          return;
        }

        const { desdeMes, desdeAnio, hastaMes, hastaAnio, fechaDesde, fechaHasta } = periodo;

        let resultado = null;
        let fuente = "local";

        // INDEX = se indexa por IPC, ICL = indice BCRA, IPC = inflacion INDEC
        try {
          if (tipoIndice === "ICL") {
            const fechaDesdeISO = `${desdeAnio}-${String(desdeMes).padStart(2, "0")}-01`;
            const fechaHastaISO = `${hastaAnio}-${String(hastaMes).padStart(2, "0")}-01`;
            const ajuste = await calcularAjusteICL(fechaDesdeISO, fechaHastaISO);
            if (ajuste) {
              resultado = { factor: ajuste.factor, porcentaje: ajuste.porcentaje, aproximado: false };
              fuente = "Argly API (BCRA)";
            }
          }
          // IPC, INDEX, or ICL fallback -> all use IPC
          if (!resultado) {
            const historial = await fetchIPCHistorial();
            const ipc = calcularFactorIPC(historial, fechaDesde, fechaHasta);
            if (ipc) {
              resultado = ipc;
              fuente = "Argly API (IPC)";
            }
          }
        } catch (apiError) {
          console.warn("API no disponible:", apiError.message);
        }

        if (!resultado) {
          let indiceTipo = tipoIndice === "INDEX" ? "CASA_PROPIA" : tipoIndice;
          const factor = await calcularIndiceAcumulado(indiceTipo, desdeAnio, desdeMes, hastaAnio, hastaMes);
          if (factor > 1) {
            resultado = { factor, porcentaje: ((factor - 1) * 100).toFixed(2), aproximado: false };
            fuente = "indices locales";
          }
        }

        if (resultado) {
          const calculado = Math.round(Number(c.montoActual) * resultado.factor);
          const primerFaltante = resultado.faltantes?.[0];
          setMontoCalculado({
            monto: calculado,
            factor: resultado.factor,
            porcentaje: resultado.porcentaje,
            porcentajePublicado: resultado.porcentajePublicado,
            desde: `${desdeMes}/${desdeAnio}`,
            hasta: `${hastaMes}/${hastaAnio}`,
            tieneIndices: true,
            fuente,
            aproximado: resultado.aproximado || false,
            publicados: resultado.publicados,
            totalMeses: resultado.totalMeses,
            faltantes: resultado.faltantes || [],
            proximaPublicacion: primerFaltante
              ? fechaPublicacionEstimada(primerFaltante.mes, primerFaltante.anio)
              : null,
          });
          setNuevoMonto(String(calculado));
        } else {
          setMontoCalculado({
            tieneIndices: false,
            desde: `${desdeMes}/${desdeAnio}`,
            hasta: `${hastaMes}/${hastaAnio}`,
          });
        }
      } catch (error) {
        console.error("Error calculando:", error);
      } finally {
        setCalculando(false);
      }
    };
    calcular();
  }, [modalContrato]);

  const aplicarAumento = async () => {
    if (!nuevoMonto || !modalContrato) return;
    setSaving(true);
    try {
      const monto = Number(nuevoMonto);
      const comision = calcularComision(monto, modalContrato.comisionPorcentaje);
      const total = calcularTotalPropietario(monto, comision);
      const fechaAumento = modalContrato.aumento.proximoAumento; // current becomes "last"
      const proximoAumento = calcularProximoAumento(
        fechaAumento,
        modalContrato.aumento.frecuencia
      );

      // El monto anterior queda registrado en `historialMontos` para que los
      // meses ya pasados sigan mostrando lo que se cobro entonces y no el
      // monto nuevo (antes se pisaba todo el historial con el ultimo valor).
      const historialMontos = agregarTramoMonto(modalContrato, {
        desde: fechaAumento,
        monto,
        comisionPorcentaje: Number(modalContrato.comisionPorcentaje) || null,
        comisionMonto: comision,
        totalPropietario: total,
        motivo: "AUMENTO",
        tipo: modalContrato.aumento?.tipo || "",
        registradoEn: new Date().toISOString(),
      });

      await updateContrato(modalContrato.idFirebase, {
        montoActual: monto,
        comisionMonto: comision,
        totalPropietario: total,
        historialMontos,
        aumento: {
          ...modalContrato.aumento,
          fechaUltimoAumento: fechaAumento,
          proximoAumento: proximoAumento, // next period
        },
      });
      await refreshContratos();
      // Clear estimation cache for this contract
      setEstimaciones((prev) => {
        const copy = { ...prev };
        delete copy[modalContrato.idFirebase];
        return copy;
      });

      // Build snapshot for the WhatsApp share card. This is the same data the
      // user just confirmed (so it doesn't depend on a context refresh).
      const porcentajeAplicado =
        Number(modalContrato.montoActual) > 0
          ? ((monto / Number(modalContrato.montoActual) - 1) * 100).toFixed(2)
          : "0";
      // Reconstruct the last 4 cycles backwards from the current adjustment.
      const historial = calcularHistorialAjustes({
        contrato: modalContrato,
        indices,
        montoFinal: monto,
        fechaFinal: fechaAumento,
        porcentajeFinal: porcentajeAplicado,
        cantidadCiclos: 4,
      });
      const telefonoLocatario =
        locatarios.find((l) => l.idFirebase === modalContrato.locatarioId)?.telefono || "";
      const snapshot = {
        contrato: { ...modalContrato, telefono: telefonoLocatario },
        montoAnterior: Number(modalContrato.montoActual),
        montoNuevo: monto,
        porcentaje: porcentajeAplicado,
        desdeStr: montoCalculado?.desde || "",
        hastaStr: montoCalculado?.hasta || "",
        fechaAumento,
        proximoAumento,
        tipoIndice: modalContrato.aumento?.tipo || "",
        frecuencia: modalContrato.aumento?.frecuencia || "",
        fuente: montoCalculado?.fuente || "",
        parcial: montoCalculado?.aproximado || false,
        mesesInfo: montoCalculado?.publicados
          ? `${montoCalculado.publicados}/${montoCalculado.totalMeses}`
          : "",
        historial,
      };
      setAjusteAplicado(snapshot);
      setModalContrato(null);

      // Persistir el comprobante para poder volver a verlo/compartirlo después.
      try {
        await saveAumentoAplicado({
          contratoId: modalContrato.idFirebase,
          locatarioNombre: modalContrato.locatarioNombre || "",
          propiedad: modalContrato.propiedad || "",
          moneda: modalContrato.moneda || "ARS",
          montoAnterior: snapshot.montoAnterior,
          montoNuevo: snapshot.montoNuevo,
          porcentaje: snapshot.porcentaje,
          desdeStr: snapshot.desdeStr,
          hastaStr: snapshot.hastaStr,
          fechaAumento: snapshot.fechaAumento,
          proximoAumento: snapshot.proximoAumento,
          tipoIndice: snapshot.tipoIndice,
          frecuencia: snapshot.frecuencia,
          fuente: snapshot.fuente,
          parcial: snapshot.parcial,
          mesesInfo: snapshot.mesesInfo,
          historial: snapshot.historial || [],
          createdAt: new Date().toISOString(),
        });
        await refreshAumentosAplicados();
        registrarActividad("AUMENTO_APLICADO", {
          mensaje: `Aumento aplicado: ${modalContrato.locatarioNombre || modalContrato.propiedad} - de ${formatMonto(snapshot.montoAnterior, snapshot.contrato?.moneda || modalContrato.moneda)} a ${formatMonto(snapshot.montoNuevo, snapshot.contrato?.moneda || modalContrato.moneda)}`,
          entidadTipo: "CONTRATO",
          entidadId: modalContrato.idFirebase,
          entidadLabel: modalContrato.propiedad,
          monto: snapshot.montoNuevo,
          moneda: modalContrato.moneda || "ARS",
        });
      } catch (e) {
        console.error("No se pudo guardar el comprobante de aumento:", e);
      }
    } catch (error) {
      console.error("Error al aplicar aumento:", error);
    } finally {
      setSaving(false);
    }
  };

  // Último comprobante de aumento guardado, por contrato (para volver a verlo).
  const ultimoComprobante = useMemo(() => {
    const m = {};
    (aumentosAplicados || []).forEach((a) => {
      const prev = m[a.contratoId];
      if (!prev || String(a.createdAt || "") > String(prev.createdAt || "")) m[a.contratoId] = a;
    });
    return m;
  }, [aumentosAplicados]);

  const verComprobante = (reg) => {
    if (!reg) return;
    setAjusteAplicado({
      contrato: { locatarioNombre: reg.locatarioNombre, propiedad: reg.propiedad, moneda: reg.moneda },
      montoAnterior: reg.montoAnterior,
      montoNuevo: reg.montoNuevo,
      porcentaje: reg.porcentaje,
      desdeStr: reg.desdeStr,
      hastaStr: reg.hastaStr,
      fechaAumento: reg.fechaAumento,
      proximoAumento: reg.proximoAumento,
      tipoIndice: reg.tipoIndice,
      frecuencia: reg.frecuencia,
      fuente: reg.fuente,
      parcial: reg.parcial,
      mesesInfo: reg.mesesInfo,
      historial: reg.historial || [],
    });
  };

  const hayIndicesCargados = indices.length > 0;

  return (
    <div className="px-4 md:px-6 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Aumentos
        </h1>
        <Link
          to="/dashboard/alquileres/indices"
          className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <FaCalculator className="w-3.5 h-3.5" /> Gestionar Indices
        </Link>
      </div>

      {!hayIndicesCargados && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 flex items-start gap-3">
          <FaInfoCircle className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-700 dark:text-amber-400">
            <p>No hay indices cargados. Sincroniza desde Argly para calcular aumentos automaticamente.</p>
            <Link to="/dashboard/alquileres/indices" className="underline font-medium">
              Cargar indices
            </Link>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        {[
          { value: "TODOS", label: "Todos" },
          { value: "VENCIDOS", label: "Vencidos" },
          { value: "ESTE_MES", label: "Este mes" },
          { value: "PROXIMO_MES", label: "Proximo mes" },
          { value: "PROXIMOS_3_MESES", label: "Proximos 3 meses" },
        ].map((f) => {
          const count = conteos[f.value] || 0;
          const isActive = filtro === f.value;
          return (
            <button
              key={f.value}
              onClick={() => setFiltro(f.value)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                isActive
                  ? "bg-primary text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
            >
              {f.label}
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 text-[11px] font-semibold rounded-full px-1.5 ${
                isActive
                  ? "bg-white/25 text-white"
                  : f.value === "VENCIDOS" && count > 0
                  ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                  : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
        {cargandoEstimaciones && (
          <span className="px-3 py-1.5 text-xs text-gray-400 flex items-center gap-1">
            <FaGlobe className="w-3 h-3 animate-pulse" /> Calculando estimaciones...
          </span>
        )}
      </div>

      {/* Busqueda */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar locatario, locador o propiedad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <button
          onClick={() => setAgrupar((v) => !v)}
          className={`inline-flex items-center gap-2 text-sm border font-medium px-3 py-2 rounded-lg transition-colors ${
            agrupar
              ? "bg-primary/10 border-primary text-primary"
              : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
          title="Agrupar los contratos por locador"
        >
          {agrupar ? "Desagrupar" : "Agrupar por locador"}
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <SortableHeader sortKey="locatarioNombre" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">Locatario</SortableHeader>
                <SortableHeader sortKey="propiedad" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">Propiedad</SortableHeader>
                <th className="text-center px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">Pago Mes</th>
                <SortableHeader sortKey="montoActual" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">Monto Actual</SortableHeader>
                <SortableHeader sortKey="estimado" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">
                  <FaArrowUp className="w-3 h-3 text-green-500" /> Estimado
                </SortableHeader>
                <SortableHeader sortKey="porcentaje" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">% Aumento</SortableHeader>
                <th className="text-center px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">Tipo</th>
                <SortableHeader sortKey="diasParaAumento" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">Prox. Aumento</SortableHeader>
                <th className="text-center px-3 py-3 font-semibold text-gray-600 dark:text-gray-400">Accion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                    {search ? "No se encontraron resultados" : "No hay aumentos pendientes"}
                  </td>
                </tr>
              ) : (
                filasRender.map((f) => {
                  if (f.tipo === "grupo") {
                    return (
                      <tr key={`grupo-${f.locador}`} className="bg-gray-100 dark:bg-gray-700/50 border-y border-gray-200 dark:border-gray-600">
                        <td colSpan={9} className="px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          {f.locador || "—"}
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            {f.items.length} contrato{f.items.length === 1 ? "" : "s"}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                  const c = f.c;
                  const est = estimaciones[c.idFirebase];
                  return (
                    <tr
                      key={c.idFirebase}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                        c.diasParaAumento < 0 ? "bg-red-50/50 dark:bg-red-900/5" : ""
                      }`}
                    >
                      <td className="px-3 py-3">
                        <p className="font-medium text-gray-800 dark:text-gray-200 text-xs">{c.locatarioNombre}</p>
                        <p className="text-[11px] text-gray-400">{c.locadorNombre}</p>
                      </td>
                      <td className="px-3 py-3 text-gray-700 dark:text-gray-300 text-xs">{c.propiedad}</td>
                      <td className="px-3 py-3 text-center">
                        {c.estadoPago === "PAGADO" ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-600 dark:text-green-400">
                            <FaCheckCircle className="w-3.5 h-3.5" /> Pago
                          </span>
                        ) : c.estadoPago === "PARCIAL" ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                            <FaCheckCircle className="w-3.5 h-3.5" /> Parcial
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-400">
                            <FaTimesCircle className="w-3.5 h-3.5" /> Debe
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-medium text-gray-800 dark:text-gray-200">
                        {formatMonto(c.montoActual, c.moneda)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {est ? (
                          <div>
                            <span
                              className={`font-bold ${est.parcial ? "text-amber-600 dark:text-amber-400" : "text-green-600 dark:text-green-400"}`}
                              title={est.parcial ? `Aproximado: ${est.totalMeses - est.mesesUsados} mes(es) proyectado(s) por promedio de los publicados` : ""}
                            >
                              {formatMonto(est.nuevoMonto, c.moneda)}
                            </span>
                            {est.parcial && (
                              <p className="text-[10px] text-amber-500">Aproximado ({est.totalMeses - est.mesesUsados} mes proyectado)</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">sin datos</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {est ? (
                          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${est.parcial ? "text-amber-600 dark:text-amber-400" : "text-green-600 dark:text-green-400"}`}>
                            <FaArrowUp className="w-2.5 h-2.5" /> {est.porcentaje}%
                            {est.parcial && <span className="font-normal">~</span>}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div>
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {c.aumento?.tipo}
                          </span>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {FREQ_LABELS[c.aumento?.frecuencia] || c.aumento?.frecuencia}
                          </p>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center text-xs whitespace-nowrap">
                        <div>
                          <span
                            className={`inline-block font-medium px-2 py-0.5 rounded-full ${
                              c.diasParaAumento < 0
                                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                : c.diasParaAumento <= 30
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-700/30 dark:text-gray-400"
                            }`}
                          >
                            {formatFechaCorta(c.aumento?.proximoAumento)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => openAplicar(c)}
                            className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-primary hover:bg-secondary text-white rounded-lg transition-colors"
                          >
                            <FaCalculator className="w-3 h-3" /> Aplicar
                          </button>
                          {ultimoComprobante[c.idFirebase] && (
                            <button
                              onClick={() => verComprobante(ultimoComprobante[c.idFirebase])}
                              className="text-emerald-500 hover:text-emerald-700 transition-colors p-1.5"
                              title="Ver comprobante del último aumento"
                            >
                              <FaReceipt className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {c.pdfContrato?.url && (
                            <a
                              href={c.pdfContrato.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-400 hover:text-red-600 transition-colors p-1.5"
                              title={`Ver PDF (${c.pdfContrato.name || "contrato.pdf"})`}
                            >
                              <FaFilePdf className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Aplicar Aumento */}
      {modalContrato && (
        <ModalPortal>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl mx-4 p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                Aplicar Aumento
              </h3>
              <button onClick={() => setModalContrato(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <FaTimes />
              </button>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-1 mb-4">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {modalContrato.locatarioNombre}
              </p>
              <p className="text-xs text-gray-500">{modalContrato.propiedad} | Locador: {modalContrato.locadorNombre}</p>
              <div className="flex items-center justify-between pt-1">
                <p className="text-sm">
                  <span className="text-gray-500">Monto actual: </span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {formatMonto(modalContrato.montoActual, modalContrato.moneda)}
                  </span>
                </p>
                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                  {modalContrato.aumento?.tipo} {FREQ_LABELS[modalContrato.aumento?.frecuencia]}
                </span>
              </div>
            </div>

            {calculando ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-4 text-sm text-blue-600 dark:text-blue-400 flex items-center gap-2">
                <FaGlobe className="w-3.5 h-3.5 animate-pulse" /> Consultando indices (Argly API)...
              </div>
            ) : montoCalculado?.tieneIndices ? (
              <div className={`rounded-lg p-3 mb-4 space-y-1 ${montoCalculado.aproximado ? "bg-amber-50 dark:bg-amber-900/20" : "bg-green-50 dark:bg-green-900/20"}`}>
                <div className="flex items-center justify-between">
                  <p className={`text-xs font-medium ${montoCalculado.aproximado ? "text-amber-700 dark:text-amber-400" : "text-green-700 dark:text-green-400"}`}>
                    Calculo automatico ({modalContrato.aumento?.tipo})
                    {montoCalculado.aproximado && <span className="ml-1 font-semibold">- Aproximado</span>}
                  </p>
                  {montoCalculado.fuente && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${montoCalculado.aproximado ? "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400" : "bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400"}`}>
                      {montoCalculado.fuente}
                    </span>
                  )}
                </div>
                <p className={`text-xs ${montoCalculado.aproximado ? "text-amber-600 dark:text-amber-400" : "text-green-600 dark:text-green-400"}`}>
                  Periodo: {montoCalculado.desde} a {montoCalculado.hasta} | Acumulado: <strong>{montoCalculado.porcentaje}%</strong>
                </p>
                {montoCalculado.aproximado && montoCalculado.faltantes?.length > 0 && (
                  <div className="bg-amber-100/60 dark:bg-amber-900/30 rounded p-2 mt-2 flex items-start gap-2">
                    <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0 w-3 h-3" />
                    <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-snug">
                      Valor aproximado. {montoCalculado.publicados}/{montoCalculado.totalMeses} meses publicados ({montoCalculado.porcentajePublicado}%).
                      Falta IPC de {montoCalculado.faltantes.map((f) => `${nombreMes(f.mes)} ${f.anio}`).join(", ")},
                      proyectado por promedio de los meses disponibles.
                      {montoCalculado.proximaPublicacion && (
                        <> Proxima publicacion INDEC: <strong>~{formatFechaCorta(montoCalculado.proximaPublicacion)}</strong>.</>
                      )}
                    </p>
                  </div>
                )}
                <p className={`text-lg font-bold ${montoCalculado.aproximado ? "text-amber-700 dark:text-amber-400" : "text-green-700 dark:text-green-400"}`}>
                  {formatMonto(montoCalculado.monto, modalContrato.moneda)}
                  <span className="text-xs font-normal ml-2">
                    (+{formatMonto(montoCalculado.monto - Number(modalContrato.montoActual), modalContrato.moneda)})
                  </span>
                </p>
              </div>
            ) : montoCalculado && !montoCalculado.tieneIndices ? (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 mb-4 space-y-1">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  No hay indices para el periodo {montoCalculado.desde} - {montoCalculado.hasta}.
                </p>
                <Link to="/dashboard/alquileres/indices" className="text-xs text-amber-700 dark:text-amber-400 underline">
                  Cargar indices
                </Link>
              </div>
            ) : null}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nuevo Monto ({modalContrato.moneda}) *
                </label>
                <MoneyInput
                  value={nuevoMonto}
                  onChange={(v) => setNuevoMonto(v === "" ? "" : String(v))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={`Actual: ${Number(modalContrato.montoActual).toLocaleString("es-AR")}`}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Podes editar el monto si el calculo automatico no es exacto.
                </p>
              </div>

              {nuevoMonto && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 space-y-1">
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Aumento: <strong>{((Number(nuevoMonto) / Number(modalContrato.montoActual) - 1) * 100).toFixed(1)}%</strong>
                    {" "}({formatMonto(Number(nuevoMonto) - Number(modalContrato.montoActual), modalContrato.moneda)})
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Nueva comision: {formatMonto(calcularComision(Number(nuevoMonto), modalContrato.comisionPorcentaje), modalContrato.moneda)}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Total propietario: {formatMonto(Number(nuevoMonto) - calcularComision(Number(nuevoMonto), modalContrato.comisionPorcentaje), modalContrato.moneda)}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
                <button onClick={() => setModalContrato(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  Cancelar
                </button>
                <button
                  onClick={aplicarAumento}
                  disabled={!nuevoMonto || saving}
                  className="px-4 py-2 text-sm bg-primary hover:bg-secondary text-white rounded-lg disabled:opacity-50"
                >
                  {saving ? "Aplicando..." : "Aplicar Aumento"}
                </button>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Share modal -- opens after a successful aplicarAumento */}
      {ajusteAplicado && (
        <AjusteShareModal
          data={ajusteAplicado}
          onClose={() => setAjusteAplicado(null)}
        />
      )}
    </div>
  );
}

export default Aumentos;
